import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "i18n-fe";
import { formatVenueLocalDateTime } from "../util/classDisplay";

// #918 — Score Admin's proof-of-send indicator: was this ride's rider
// actually emailed their score sheet? Mirrors SheetStateChip: semantic MUI
// Chip colors only (never `primary` — dark grey, disappears in dark mode).
//
// Derived from report-api's GET /score-sheets/rides/:rideId/status response
// ({ requested, status, sheetEmailedAt }). The load-bearing field is
// `sheetEmailedAt` — it's only stamped after a real email send is CONFIRMED
// (see report-api's sheet-request.js markSheetEmailed), never on a merely
// dispatched/attempted send. That's what makes NOT_SENT/PENDING distinct from
// SENT here, rather than collapsing "requested" into "sent".
export function deriveSheetSentState(statusData) {
  // Default-parameter destructuring only covers `undefined`, not an explicit
  // `null` — and `null` is exactly what callers pass before the status fetch
  // resolves (see RideEditor's `sheetSentStatus` initial state), so this must
  // guard both.
  const { requested, status, sheetEmailedAt } = statusData || {};
  if (sheetEmailedAt) return "SENT";
  if (status === "FAILED") return "FAILED";
  if (requested) return "PENDING";
  return "NOT_SENT";
}

const SHEET_SENT_DISPLAY = {
  NOT_SENT: { color: "default", variant: "outlined", labelKey: "rideEditor.sheetSent.notSent" },
  PENDING: { color: "info", variant: "outlined", labelKey: "rideEditor.sheetSent.pending" },
  SENT: { color: "success", variant: "filled", labelKey: "rideEditor.sheetSent.sent" },
  FAILED: { color: "error", variant: "outlined", labelKey: "rideEditor.sheetSent.failed" },
};

export default function SheetSentChip({ statusData, size = "small", ...props }) {
  const { t } = useTranslation("scoreadmin");
  const state = deriveSheetSentState(statusData);
  const display = SHEET_SENT_DISPLAY[state];
  const chip = (
    <Chip
      size={size}
      color={display.color}
      variant={display.variant}
      label={t(display.labelKey)}
      data-testid="sheet-sent-chip"
      data-sheet-sent-state={state}
      {...props}
    />
  );
  const sentAt = statusData?.sheetEmailedAt ? formatVenueLocalDateTime(statusData.sheetEmailedAt) : null;
  if (!sentAt) return chip;
  return (
    <Tooltip title={t("rideEditor.sheetSent.sentAtTooltip", { when: sentAt })}>
      <span>{chip}</span>
    </Tooltip>
  );
}
